import i18n from '@/utils/i18n';
import { GetPricingHubAgentRecsDocument } from '@sales-portal/domain/pricing/screens/overview/blocs/documents/pricing-hub-agent-recs.generated';
import { GetPricingRecommendationsDocument } from '@/experiences/sales-portal/domain/pricing-agent/blocs/documents/get-pricing-recommendations.generated';
import { GetPricingTaskRunsByTypeForBusinessOrganizationDocument } from '@/experiences/sales-portal/domain/pricing-agent/blocs/documents/get-pricing-task-runs-by-type-for-business-organization.generated';
import {
  RecommendationAction as LocalRecommendationAction,
  RecommendationActionsSnapshot,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { mapAlertLevelToToastVariant } from '@sales-portal/domain/pricing/screens/management/blocs/utils/toast-utils';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { toActionInput } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/recommendation-action-input';
import { useCallback, useMemo } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSubmitPricingRecommendationActionsMutation } from '@/experiences/sales-portal/domain/pricing-agent/blocs/documents/submit-pricing-recommendation-actions.generated';

interface UseSubmitRecommendationActionsMutatorParams {
  taskTypeUUID: string;
}

export const useSubmitRecommendationActionsMutator = ({
  taskTypeUUID,
}: UseSubmitRecommendationActionsMutatorParams) => {
  const { showErrorToast, showToast } = useExperienceToasts();
  const [submit, { loading }] = useSubmitPricingRecommendationActionsMutation();

  const submitActions = useCallback(
    async (actions: RecommendationActionsSnapshot): Promise<boolean> => {
      const inputs = Object.values(actions).map(toActionInput);
      if (inputs.length === 0) {
        return false;
      }
      const { acceptedCount, overriddenCount, rejectedCount } = Object.values(
        actions,
      ).reduce(
        (counts, entry) => {
          switch (entry.action) {
            case LocalRecommendationAction.Accepted:
              counts.acceptedCount += 1;
              break;
            case LocalRecommendationAction.Overridden:
              counts.overriddenCount += 1;
              break;
            case LocalRecommendationAction.Rejected:
              counts.rejectedCount += 1;
              break;
          }
          return counts;
        },
        { acceptedCount: 0, overriddenCount: 0, rejectedCount: 0 },
      );
      try {
        const { data } = await submit({
          awaitRefetchQueries: true,
          refetchQueries: [
            GetPricingRecommendationsDocument,
            GetPricingTaskRunsByTypeForBusinessOrganizationDocument,
            GetPricingHubAgentRecsDocument,
          ],
          variables: { actions: inputs },
        });
        const perSupplier =
          data?.submitPricingRecommendationActionsForBusinessOrganization
            .alerts_by_supplier ?? [];
        const alerts = perSupplier.flatMap((entry) =>
          entry.alert ? [entry.alert] : [],
        );
        if (alerts.length > 0) {
          alerts.forEach((alert) =>
            showToast({
              title: alert.alert_message,
              variant: mapAlertLevelToToastVariant(alert.alert_level),
            }),
          );
        } else {
          showToast({
            title: i18n.t(
              `pricing.agent.publish.success_message.${inputs.length === 1 ? 'singular' : 'plural'}`,
              {
                count: inputs.length,
              },
            ),
            variant: 'success',
          });
        }
        pricingManagementAnalytics.track(PricingAnalyticEvent.AGENT_PUBLISH, {
          acceptedCount,
          overriddenCount,
          rejectedCount,
          supplierUUIDs: [
            ...new Set(
              Object.values(actions)
                .map((entry) => entry.supplierUUID)
                .filter(Boolean),
            ),
          ],
          taskTypeUUID,
        });
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : i18n.t('pricing.verify_edits.unknown_error');
        showErrorToast(errorMessage);
        pricingManagementAnalytics.track(
          PricingAnalyticEvent.AGENT_PUBLISH_ERROR,
          {
            error: errorMessage,
            taskTypeUUID,
          },
        );
        return false;
      }
    },
    [showErrorToast, showToast, submit, taskTypeUUID],
  );

  return useMemo(() => ({ loading, submitActions }), [loading, submitActions]);
};
